import { useState, useEffect } from 'react';
import {
  fetchCompanyFile,
  findClientConfig,
  getClientCities,
  getClientCityLoadPlan,
  getJsonFileForCity,
  getGeojsonFileForCity,
} from '../services/companyFileReader';


// ── Client-Side Normalizer: converts raw JSON → internal format ─────────────

function normalizeKey(str) {
  return str
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[àá]/g, "a").replace(/[èé]/g, "e").replace(/[ìí]/g, "i")
    .replace(/[òó]/g, "o").replace(/[ùú]/g, "u")
    .replace(/[^a-z0-9_]/g, "");
}

/** Lowercase keys in fermata/linea/punto-vendita detail records for consistent UI lookups */
function normalizeDetailRecord(record) {
  if (!record || typeof record !== "object" || Array.isArray(record)) return record;
  const out = {};
  for (const [key, value] of Object.entries(record)) {
    const lk = key.toLowerCase();
    if (value && typeof value === "object" && !Array.isArray(value)) {
      out[lk] = normalizeDetailRecord(value);
    } else {
      out[lk] = value;
    }
  }
  return out;
}

function normalizeDettaglioMap(rawDettaglio) {
  if (!rawDettaglio || typeof rawDettaglio !== "object") return rawDettaglio;
  const out = {};
  for (const [id, record] of Object.entries(rawDettaglio)) {
    out[id] = normalizeDetailRecord(record);
  }
  return out;
}

function stampAmbitoOnDettaglio(dettaglio, forceExtraurban = false) {
  if (!dettaglio || typeof dettaglio !== "object") return dettaglio;
  for (const id of Object.keys(dettaglio)) {
    const rec = dettaglio[id];
    if (!rec || typeof rec !== "object") continue;
    if (forceExtraurban) rec.isExtraurban = true;
    else if (String(rec.ambito ?? rec.Ambito ?? "").toLowerCase().includes("extra")) {
      rec.isExtraurban = true;
    }
  }
  return dettaglio;
}

function normalizeCategory(raw) {
  let punteggio_globale = null;
  const sub = {};
  const grafici = {};
  const linee = [];
  let dettaglio = null;

  for (const [key, value] of Object.entries(raw)) {
    const lk = key.toLowerCase();

    if (lk.startsWith("punteggio globale")) {
      punteggio_globale = value;
      continue;
    }

    if (lk.startsWith("dettaglio ")) {
      if (typeof value === "object" && value !== null) {
        dettaglio = normalizeDettaglioMap(value);
        if (lk === "dettaglio linee") {
          linee.push(...Object.keys(dettaglio));
        }
      }
      continue;
    }

    if (typeof value !== "object" || value === null) continue;

    const v = value;
    const nk = normalizeKey(key);
    const subScore = v["Voto categoria"] ?? null;
    const breakdown = {};
    const graficiGroup = {};

    for (const [subKey, subVal] of Object.entries(v)) {
      if (subKey === "Voto categoria" || subKey === "Grafico") continue;
      if (typeof subVal !== "object" || subVal === null) continue;

      const sv = subVal;

      if ("Voto microcategoria" in sv) {
        breakdown[subKey] = sv["Voto microcategoria"];
        if (typeof sv["Grafico"] === "object" && sv["Grafico"] !== null) {
          graficiGroup[subKey] = sv["Grafico"];
        } else if (typeof sv["Grafico"] === "number") {
          graficiGroup[subKey] = sv["Grafico"];
        }
      } else if ("Grafico" in sv && typeof sv["Grafico"] === "object" && sv["Grafico"] !== null) {
        graficiGroup[subKey] = sv["Grafico"];
      } else if (typeof sv["Grafico"] === "number") {
        graficiGroup[subKey] = sv["Grafico"];
      } else {
        // Sub-group: scan one level deeper
        const deepEntries = Object.entries(sv).filter(([k]) => k !== "Grafico");
        const allNumeric = deepEntries.length > 0 && deepEntries.every(([, dv]) => typeof dv === "number");
        
        if (allNumeric) {
          // Flat numeric map (e.g., "Medie": { "Accoglienza": 8.4, ... })
          graficiGroup[subKey] = Object.fromEntries(deepEntries);
        } else {
          for (const [deepKey, deepVal] of deepEntries) {
            if (typeof deepVal !== "object" || deepVal === null) continue;
            const dv = deepVal;
            if ("Voto microcategoria" in dv) {
              breakdown[deepKey] = dv["Voto microcategoria"];
              if (typeof dv["Grafico"] === "object" && dv["Grafico"] !== null) {
                graficiGroup[deepKey] = dv["Grafico"];
              } else if (typeof dv["Grafico"] === "number") {
                graficiGroup[deepKey] = dv["Grafico"];
              }
            } else if ("Grafico" in dv) {
              // Items with only "Grafico" and no "Voto microcategoria"
              if (typeof dv["Grafico"] === "object" && dv["Grafico"] !== null) {
                graficiGroup[deepKey] = dv["Grafico"];
              } else if (typeof dv["Grafico"] === "number") {
                graficiGroup[deepKey] = dv["Grafico"];
              }
            }
          }
        }
      }
    }

    const subEntry = { punteggio_globale: subScore };
    if (Object.keys(breakdown).length > 0) subEntry.breakdown = breakdown;
    sub[nk] = subEntry;
    if (Object.keys(graficiGroup).length > 0) grafici[nk] = graficiGroup;
  }

  const result = { punteggio_globale };
  if (Object.keys(sub).length > 0) result.sub = sub;
  if (Object.keys(grafici).length > 0) result.grafici = grafici;
  if (linee.length > 0) result.linee = linee;
  if (dettaglio) result.dettaglio = dettaglio;
  return result;
}

export function normalizeCompanyIfNeeded(raw) {
  if (typeof raw !== "object" || raw === null) return raw;
  if (raw.citta) return raw; // already in internal format

  const rawCitta = raw["città"] || raw["citta"];
  if (!rawCitta || typeof rawCitta !== "object") return raw;

  const citta = {};
  for (const [cityName, cityRaw] of Object.entries(rawCitta)) {
    if (typeof cityRaw !== "object" || cityRaw === null) continue;
    const cr = cityRaw;
    const city = {};
    const spatialKeys = [
      "fermate", "fermate_extraurbane",
      "punti_vendita", "punti_vendita_extraurbane",
      "linee_urbane", "linee_extraurbane",
      "call_center", "sito_web", "app",
    ];
    for (const mk of spatialKeys) {
      if (cr[mk] && typeof cr[mk] === "object") {
        const normalized = normalizeCategory(cr[mk]);
        const isExtraCategory = mk.endsWith("_extraurbane");
        if (normalized.dettaglio) {
          stampAmbitoOnDettaglio(normalized.dettaglio, isExtraCategory);
        }
        city[mk] = normalized;
      }
    }
    // Store group-level scores from raw JSON
    const fisiciKey = Object.keys(cr).find(k => k.toLowerCase().includes("servizi fisici"));
    if (fisiciKey) city.punteggio_servizi_fisici = cr[fisiciKey];
    // Store per-city indice sintetico
    const indiceCityKey = Object.keys(cr).find(k => k.toLowerCase().trim().replace(/\s+/g, '_') === 'indice_sintetico');
    if (indiceCityKey !== undefined) city.indice_sintetico = cr[indiceCityKey];
    citta[cityName] = city;
  }

  const parseNumber = (val) => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      const parsed = parseFloat(val.replace(',', '.'));
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  // Service-level scores (top-level or per-city)
  const digitaliKey = Object.keys(raw).find(k => k.toLowerCase().includes("servizi digitali"));
  const punteggio_servizi_digitali = digitaliKey ? parseNumber(raw[digitaliKey]) : null;

  const indiceSinteticoKey = Object.keys(raw).find(
    k => k.toLowerCase().trim().replace(/\s+/g, '_') === 'indice_sintetico'
  );
  const indice_sintetico = indiceSinteticoKey ? parseNumber(raw[indiceSinteticoKey]) : null;

  for (const cityName of Object.keys(citta)) {
    const city = citta[cityName];
    city.punteggio_servizi_fisici = parseNumber(city.punteggio_servizi_fisici);
    const parsedCityIndice = parseNumber(city.indice_sintetico);
    // Each city file may keep its score at the top level rather than in the city object.
    city.indice_sintetico = parsedCityIndice ?? indice_sintetico;
  }

  return {
    anno: raw.anno,
    azienda: raw.azienda,
    indice_sintetico,
    punteggio_servizi_digitali,
    citta,
  };
}

/** Merge multiple normalized company records (one per city JSON) into a single record */
export function mergeCompanyRecords(records, year) {
  if (!records.length) return null;

  const merged = {
    ...records[0],
    anno: year,
    citta: { ...(records[0].citta || {}) },
  };

  for (let i = 1; i < records.length; i += 1) {
    const record = records[i];
    merged.citta = { ...merged.citta, ...(record.citta || {}) };
    if (merged.indice_sintetico === null && record.indice_sintetico !== null && record.indice_sintetico !== undefined) {
      merged.indice_sintetico = record.indice_sintetico;
    }
    if (merged.punteggio_servizi_digitali === null && record.punteggio_servizi_digitali !== null && record.punteggio_servizi_digitali !== undefined) {
      merged.punteggio_servizi_digitali = record.punteggio_servizi_digitali;
    }
    if (!merged.azienda && record.azienda) {
      merged.azienda = record.azienda;
    }
  }

  return merged;
}

/** Load and merge all city JSON files for a client/year */
export async function loadClientYearData(clientConf, year) {
  const plan = getClientCityLoadPlan(clientConf);
  if (!plan.length) return null;

  const normalizedList = [];
  for (const { jsonFile } of plan) {
    try {
      const raw = await fetchCompanyFile(clientConf.company, jsonFile, year);
      normalizedList.push({ ...normalizeCompanyIfNeeded(raw), anno: year });
    } catch {
      // Missing city/year files are expected for incomplete uploads.
    }
  }

  return mergeCompanyRecords(normalizedList, year);
}

/**
 * useClientData — loads city-specific analytics JSON and optional GTFS geometry.
 * For multi-city clients, geojson loads only when a city is selected.
 */
export const useClientData = (companyName, year, selectedCity) => {
  const [clientData, setClientData] = useState(null);
  const [geojsonData, setGeojsonData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyName || !year) {
      setLoading(true);
      setError(null);
      return undefined;
    }

    let isMounted = true;
    const targetCompany = companyName;
    const clientConf = findClientConfig(targetCompany);
    const cities = getClientCities(targetCompany);
    const isMultiCity = cities.length > 1;
    const targetCity = selectedCity || (!isMultiCity ? cities[0] : null);

    if (!clientConf || getClientCityLoadPlan(clientConf).length === 0) {
      setError(`CLIENTE NON VALIDO: Impossibile trovare i dati per "${targetCompany}".`);
      setLoading(false);
      return undefined;
    }

    if (!targetCity) {
      setClientData(null);
      setGeojsonData(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const jsonFile = getJsonFileForCity(targetCompany, targetCity);
    const geojsonFile = getGeojsonFileForCity(targetCompany, targetCity);

    if (!jsonFile) {
      setError(`Dati non configurati per la città "${targetCity}".`);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const loadData = async () => {
      try {
        const rawData = await fetchCompanyFile(targetCompany, jsonFile, year);
        const normalizedData = { ...normalizeCompanyIfNeeded(rawData), anno: year };

        let features = null;
        if (geojsonFile) {
          try {
            const rawGeo = await fetchCompanyFile(targetCompany, geojsonFile, year);
            features = rawGeo.features || rawGeo;
          } catch {
            // Optional GTFS/line geometry may be missing for this city.
          }
        }

        if (isMounted) {
          setClientData(normalizedData);
          setGeojsonData(features);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(`Errore nel caricamento dei dati per "${targetCompany}": ${err.message}`);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [companyName, year, selectedCity]);

  return { clientData, geojsonData, error, loading };
};
